import React, { useState, useEffect } from "react";
import {
  Box, Grid, Typography, Paper, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Divider, MenuItem, Select,
  FormControl, InputLabel, InputAdornment, CircularProgress, Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon, Delete as DeleteIcon, Visibility as PreviewIcon,
  ArrowBack as ArrowBackIcon, Save as SaveIcon,
} from "@mui/icons-material";
import { apiList, invokeApi, invokeGetApi } from "../../../services/apiServices";
import { config } from "../../../config/config";
import { useNavigate, useParams } from "react-router-dom";
import { useCookies } from "react-cookie";
import { products } from "../../../config/products";
import { InvoiceModal, PURPLE } from "../../../componants/common/InvoiceDocument";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";

const CreateInvoice = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [cookies] = useCookies();
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Fetch registered customer users on mount
  useEffect(() => {
    invokeApi(config.apiUrl + apiList.getUsers, { role: "Customer" }, cookies)
      .then(res => {
        const users = res?.data?.users || res?.data?.data?.users || [];
        setCustomers(users.filter(u => !u.roles?.includes("Admin") && !u.roles?.includes("Super Admin")));
      })
      .catch(() => {});
  }, []);

  const [invoice, setInvoice] = useState({
    customerName: "", customerNumber: "", customerEmail: "", address: "",
    dueDate: new Date().toISOString().split("T")[0],
    notes: "Thank you for choosing AR Infotech Solutions! All software licenses come with genuine lifetime activation support.",
    paymentStatus: "Paid", items: [], subtotal: 0, total: 0, received: 0, balance: 0,
    userId: null,
  });

  const [newItem, setNewItem] = useState({ description: "", hsn_sac: "997331", quantity: 1, unit: "Pcs", unitPrice: "", totalPrice: 0 });

  const handleInvoiceChange = (e) => {
    const { name, value } = e.target;
    setInvoice((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "received") {
        const receivedVal = parseFloat(value) || 0;
        updated.balance = Math.max(0, (parseFloat(prev.total) || 0) - receivedVal);
      }
      return updated;
    });
  };

  // When customer is selected from dropdown, auto-fill fields
  const handleCustomerSelect = (event, customer) => {
    setSelectedCustomer(customer);
    if (customer) {
      setInvoice(prev => ({
        ...prev,
        customerName: `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || customer.name || customer.email || "",
        customerNumber: customer.phone || customer.mobile || customer.customerNumber || "",
        customerEmail: customer.email || "",
        address: customer.address || "",
        userId: customer.id || customer._id || null,
      }));
    }
  };

  const handleSelectPresetProduct = (productId) => {
    const p = products.find((item) => item.id === productId);
    if (p) {
      const qty = 1; const price = parseFloat(p.price) || 0;
      setNewItem({ description: `${p.name} - Genuine Retail License`, hsn_sac: "997331", quantity: qty, unit: "Pcs", unitPrice: price, totalPrice: qty * price });
    }
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setNewItem((prev) => {
      const updated = { ...prev, [name]: value };
      const qty = parseFloat(name === "quantity" ? value : updated.quantity) || 0;
      const price = parseFloat(name === "unitPrice" ? value : updated.unitPrice) || 0;
      updated.totalPrice = qty * price;
      return updated;
    });
  };

  const addItem = () => {
    if (!newItem.description.trim()) { alert("Please enter or select an item description."); return; }
    const qty = parseFloat(newItem.quantity) || 0;
    const price = parseFloat(newItem.unitPrice) || 0;
    if (qty <= 0 || price < 0) { alert("Please enter a valid quantity and unit price."); return; }
    const itemToAdd = { ...newItem, quantity: qty, unitPrice: price, totalPrice: qty * price };
    const updatedItems = [...invoice.items, itemToAdd];
    const subtotal = updatedItems.reduce((sum, i) => sum + (parseFloat(i.totalPrice) || 0), 0);
    const total = subtotal;
    const received = invoice.received === 0 || invoice.received === "" ? total : parseFloat(invoice.received);
    const balance = Math.max(0, total - received);
    setInvoice((prev) => ({ ...prev, items: updatedItems, subtotal, total, received, balance }));
    setNewItem({ description: "", hsn_sac: "997331", quantity: 1, unit: "Pcs", unitPrice: "", totalPrice: 0 });
  };

  const removeItem = (index) => {
    const updatedItems = invoice.items.filter((_, i) => i !== index);
    const subtotal = updatedItems.reduce((sum, i) => sum + (parseFloat(i.totalPrice) || 0), 0);
    const total = subtotal;
    const received = Math.min(parseFloat(invoice.received) || 0, total);
    const balance = Math.max(0, total - received);
    setInvoice((prev) => ({ ...prev, items: updatedItems, subtotal, total, received, balance }));
  };

  const handleSubmit = async () => {
    if (!invoice.customerName.trim() || !invoice.customerNumber.trim()) { alert("Please fill in Customer Name and Contact Number."); return; }
    if (invoice.items.length === 0) { alert("Please add at least one line item to the invoice."); return; }
    setLoading(true);
    try {
      const payload = {
        customerName: invoice.customerName, customerNumber: invoice.customerNumber,
        customerEmail: invoice.customerEmail, address: invoice.address, dueDate: invoice.dueDate,
        notes: invoice.notes, paymentStatus: invoice.paymentStatus, subtotal: invoice.subtotal,
        total: invoice.total, received: invoice.received, balance: invoice.balance,
        userId: invoice.userId,
        items: invoice.items.map((i) => ({ description: i.description, hsn_sac: i.hsn_sac || "997331", quantity: i.quantity, unit: i.unit || "Pcs", unitPrice: i.unitPrice, totalPrice: i.totalPrice })),
      };
      const res = await invokeApi(config.getMyCollege + apiList.createInvoice, payload, cookies);
      if (res?.status === 200 || res?.responseCode === "200") {
        alert(`Invoice Created Successfully! Invoice No: ${res?.invoiceNumber || ""}`);
        navigate("/invoice-manager/invoices");
      } else {
        alert(res?.responseMessage || "Failed to create invoice.");
      }
    } catch (err) { alert("An error occurred while creating invoice."); }
    finally { setLoading(false); }
  };

  const previewInvoiceData = {
    customer_name: invoice.customerName || "Customer Name", customer_number: invoice.customerNumber || "9876543210",
    customer_email: invoice.customerEmail, address: invoice.address || "Bangalore, Karnataka",
    due_date: invoice.dueDate, invoice_number: "AR-IN-PREVIEW", payment_status: invoice.paymentStatus,
    items: invoice.items.map((i) => ({ description: i.description, hsn_sac: i.hsn_sac || "997331", quantity: i.quantity, unit: i.unit, unit_price: i.unitPrice, total_price: i.totalPrice })),
    subtotal: invoice.subtotal, total: invoice.total, received: invoice.received, balance: invoice.balance,
  };

  return (
    <Box sx={{ display: "flex", bgcolor: "#F8FAFC", minHeight: "100vh" }}>
      <Sidenav />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Header />
        <Box sx={{ mt: 2 }}>
          {/* Header bar */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate("/invoice-manager/invoices")}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}>
                Back to Invoices
              </Button>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1A237E" }}>Create New Tax Invoice</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button variant="outlined" startIcon={<PreviewIcon />} onClick={() => setPreviewOpen(true)}
                disabled={invoice.items.length === 0}
                sx={{ borderColor: PURPLE, color: PURPLE, textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}>
                Live Preview
              </Button>
              <Button variant="contained"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={handleSubmit} disabled={loading}
                sx={{ backgroundColor: PURPLE, "&:hover": { backgroundColor: "#6454DB" }, textTransform: "none", fontWeight: 700, borderRadius: 1.5, px: 3 }}>
                Save & Generate Invoice
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* Customer Information Card */}
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #E2E8F0" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1A237E", mb: 2 }}>1. Customer & Invoice Info</Typography>

                {/* Customer Search Dropdown */}
                <Box sx={{ mb: 2.5, p: 2, bgcolor: "#EEF2FF", borderRadius: 2, border: "1px dashed #C7D2FE" }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#4338CA", mb: 1 }}>
                    🔍 Search & Select Registered Customer (Optional)
                  </Typography>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(c) =>
                      `${(c.firstName || c.name || "")} ${c.lastName || ""} — ${c.email || ""} ${c.phone ? "(" + c.phone + ")" : ""}`.trim()
                    }
                    value={selectedCustomer}
                    onChange={handleCustomerSelect}
                    inputValue={customerSearch}
                    onInputChange={(_, val) => setCustomerSearch(val)}
                    renderInput={(params) => (
                      <TextField {...params} label="Type customer name, email, or phone..." size="small" fullWidth />
                    )}
                    noOptionsText="No registered customers found"
                    filterOptions={(options, state) => {
                      const q = state.inputValue.toLowerCase();
                      return options.filter(c =>
                        (c.name || c.firstName || c.email || "").toLowerCase().includes(q) ||
                        (c.email || "").toLowerCase().includes(q) ||
                        (c.phone || "").includes(q)
                      );
                    }}
                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                  />
                  {selectedCustomer && (
                    <Typography variant="caption" sx={{ color: "#16A34A", mt: 0.5, display: "block" }}>
                      ✅ Customer selected — fields auto-filled below. Invoice link will be sent to their account.
                    </Typography>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Customer / Company Name *" name="customerName"
                      value={invoice.customerName} onChange={handleInvoiceChange} placeholder="e.g. John Doe / Tech Solutions" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Customer Contact Phone *" name="customerNumber"
                      value={invoice.customerNumber} onChange={handleInvoiceChange} placeholder="e.g. 9876543210" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Customer Email" name="customerEmail"
                      value={invoice.customerEmail} onChange={handleInvoiceChange} placeholder="e.g. customer@example.com" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" type="date" label="Invoice Date *" name="dueDate"
                      value={invoice.dueDate} onChange={handleInvoiceChange} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" multiline rows={2} label="Billing / Shipping Address" name="address"
                      value={invoice.address} onChange={handleInvoiceChange}
                      placeholder="e.g. Unit 402, Tech Park, Indiranagar, Bangalore, Karnataka - 560038" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Payment Status</InputLabel>
                      <Select label="Payment Status" name="paymentStatus" value={invoice.paymentStatus} onChange={handleInvoiceChange}>
                        <MenuItem value="Paid">Paid (Full)</MenuItem>
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Partial">Partial</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Company & Bank Reference Card */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #E2E8F0", bgcolor: "#FAFAFE", height: "100%" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1A237E", mb: 1 }}>AR Infotech Solutions Details</Typography>
                <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6, fontSize: "12.5px" }}>
                  <strong>GSTIN:</strong> 29BTWPA6587J1ZQ (Karnataka)<br />
                  <strong>Bank:</strong> State Bank Of India, RBI Layout, Bangalore<br />
                  <strong>A/C No:</strong> 45375033092 &nbsp;|&nbsp; <strong>IFSC:</strong> SBIN0004408<br />
                  <strong>Email:</strong> arinfotechsolution11@gmail.com &nbsp;|&nbsp; <strong>Phone:</strong> 9901991771
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" sx={{ color: "#64748B" }}>
                  * Standard software tax invoices are issued under SAC 997331 (Licensing services for software applications).
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ bgcolor: "#EEF2FF", borderRadius: 1.5, p: 1.5 }}>
                  <Typography variant="caption" sx={{ color: "#4338CA", fontWeight: 700, display: "block", mb: 0.5 }}>
                    📌 Customer Invoice Link
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#475569" }}>
                    After saving, the customer will be able to download/view their invoice from their <strong>Order History</strong> page if linked to a user account. A public share link is also generated automatically.
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Line Item Creator */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #E2E8F0" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1A237E", mb: 2 }}>2. Add Invoice Line Items</Typography>

                {/* Quick Catalog Selector */}
                <Box sx={{ mb: 2.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#475569" }}>Quick Product Selector:</Typography>
                  <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel>Choose from Catalog</InputLabel>
                    <Select label="Choose from Catalog" defaultValue="" onChange={(e) => handleSelectPresetProduct(e.target.value)}>
                      {products.map((prod) => <MenuItem key={prod.id} value={prod.id}>{prod.name} (₹{prod.price})</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Item Description *" name="description"
                      value={newItem.description} onChange={handleItemChange}
                      placeholder="e.g. Windows 11 Pro Retail Lifetime Key" />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" label="HSN/SAC" name="hsn_sac" value={newItem.hsn_sac} onChange={handleItemChange} />
                  </Grid>
                  <Grid item xs={6} sm={1.5}>
                    <TextField fullWidth size="small" type="number" label="Qty *" name="quantity" value={newItem.quantity} onChange={handleItemChange} inputProps={{ min: 1 }} />
                  </Grid>
                  <Grid item xs={6} sm={1.5}>
                    <TextField fullWidth size="small" label="Unit" name="unit" value={newItem.unit} onChange={handleItemChange} />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" type="number" label="Unit Price (₹) *" name="unitPrice" value={newItem.unitPrice} onChange={handleItemChange}
                      InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Button fullWidth variant="contained" onClick={addItem}
                      sx={{ backgroundColor: PURPLE, "&:hover": { backgroundColor: "#6454DB" }, py: 1, textTransform: "none", fontWeight: 700 }}>
                      Add
                    </Button>
                  </Grid>
                </Grid>

                {/* Line Items Table */}
                <TableContainer sx={{ mt: 3, border: "1px solid #E2E8F0", borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: PURPLE }}>
                        {["#", "Item Name", "HSN/SAC", "Qty", "Unit", "Price/Unit", "Amount", "Action"].map(h => (
                          <TableCell key={h} sx={{ color: "#fff", fontWeight: 700 }} align={["Qty", "Unit", "Price/Unit", "Amount"].includes(h) ? "right" : "left"}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.items.length > 0 ? (
                        invoice.items.map((item, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{item.description}</TableCell>
                            <TableCell>{item.hsn_sac}</TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">{item.unit}</TableCell>
                            <TableCell align="right">₹ {parseFloat(item.unitPrice).toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>₹ {parseFloat(item.totalPrice).toFixed(2)}</TableCell>
                            <TableCell align="center">
                              <IconButton size="small" color="error" onClick={() => removeItem(idx)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 3, color: "#94A3B8" }}>
                            No items added yet. Choose a product above or type custom details.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Summary Block */}
                {invoice.items.length > 0 && (
                  <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                    <Paper variant="outlined" sx={{ width: { xs: "100%", sm: "360px" }, p: 2, borderRadius: 2, bgcolor: "#FAFAFE" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>Sub Total:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>₹ {invoice.subtotal.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 800, color: "#1A237E" }}>Total Amount:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 800, color: "#1A237E" }}>₹ {invoice.total.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="body2" sx={{ color: "#475569" }}>Received:</Typography>
                        <TextField size="small" type="number" name="received" value={invoice.received}
                          onChange={handleInvoiceChange} sx={{ width: "120px" }}
                          inputProps={{ style: { textAlign: "right", padding: "4px 8px" } }} />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Balance Due:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: invoice.balance > 0 ? "#DC2626" : "#16A34A" }}>
                          ₹ {invoice.balance.toFixed(2)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Live Preview Modal */}
      <InvoiceModal open={previewOpen} onClose={() => setPreviewOpen(false)} data={previewInvoiceData} />
    </Box>
  );
};

export default CreateInvoice;
